import { z } from 'zod';
import { flow } from 'fp-ts/lib/function';
import { FieldType } from 'modules/queryBuilder/types';
import {
  validateDateValue,
  validateRegexValue,
  validateNumberValue,
  validateEmptyValue,
  validateEmptyComparator,
} from 'modules/queryBuilder/schemas/utils';
import { DateRange } from 'modules/queryBuilder/types';
import { useListId } from 'modules/segmentation/hooks';
import { dateRangeSchema } from 'modules/queryBuilder/schemas/condition/dateRange';
import {
  numberValueCondition,
  booleanValueCondition,
  undefinedValueCondition,
  dateValueCondition,
  geoLocationValueCondition,
  nestedValueCondition,
  stringValueCondition,
  campaignLabelIdCondition,
} from 'modules/queryBuilder/schemas';
import { transformCampaignLabelCondition } from 'modules/queryBuilder/schemas/utils';
import { MatchesCatalogComparator } from 'modules/segmentation/types';
import { isObject } from 'utils/helpers';

import { useValidateObjectDataTypeCondition } from './useValidateObjectDataTypeCondition';
import { useValidateEmptyValues } from './useValidateEmptyValues';
import { useTransformToUnsubscribedMessageCondition } from './useTransformToUnsubscribedMessageCondition';
import { segmentationComparatorSchema } from '../comparator';
import { transformCatalogCondition } from '../utils';
import { isCatalogQueryValue } from '../../catInSeg/utils';

export const catalogQueryValue = z.object({
  catalogField: z.ostring(),
  catalogQuery: z.object({
    catalogName: z.string(),
    searchQuery: z.record(z.string(), z.unknown()),
    sizeLimit: z.number().optional(),
  }),
});

const createSegmentationStringValueCondition = (listId?: number) =>
  z.object({
    ...stringValueCondition.shape,
    comparatorType: segmentationComparatorSchema.optional(),
    value: z.string().or(catalogQueryValue).optional(),
    ...(listId && { values: z.array(z.string()).optional() }),
  });

const createSegmentationNumberValueCondition = (listId?: number) =>
  z.object({
    ...numberValueCondition.shape,
    comparatorType: segmentationComparatorSchema.optional(),
    value: z.string().or(z.number()).pipe(z.coerce.string()).or(catalogQueryValue).optional(),
    ...(listId && { values: z.array(z.string()).optional() }),
  });

const segmentationBooleanValueCondition = z.object({
  ...booleanValueCondition.shape,
  comparatorType: segmentationComparatorSchema.optional(),
  value: z.string().or(catalogQueryValue).optional(),
});

const segmentationDateValueCondition = z.object({
  ...dateValueCondition.shape,
  comparatorType: segmentationComparatorSchema.optional(),
  value: z.string().or(dateRangeSchema).or(catalogQueryValue).optional(),
});

export const useSegmentationConditionSchema = () => {
  const listId = useListId();
  const validateObjectDataTypeCondition = useValidateObjectDataTypeCondition();
  const validateEmptyValues = useValidateEmptyValues();

  const segmentationStringValueCondition = createSegmentationStringValueCondition(listId);
  const segmentationNumberValueCondition = createSegmentationNumberValueCondition(listId);

  const transformToUnsubscribedMessageCondition = useTransformToUnsubscribedMessageCondition();

  return z
    .discriminatedUnion('fieldType', [
      segmentationStringValueCondition,
      segmentationNumberValueCondition,
      segmentationBooleanValueCondition,
      undefinedValueCondition,
      segmentationDateValueCondition,
      geoLocationValueCondition,
      nestedValueCondition,
      campaignLabelIdCondition,
    ])
    .superRefine((condition, { addIssue, path }) => {
      const { value, values, comparatorType, fieldType, castType } = condition;
      validateEmptyValue({ value, values }, addIssue);
      validateObjectDataTypeCondition({ fieldType, comparatorType }, path);
      validateEmptyValues({ values }, path);
      validateEmptyComparator({ comparatorType }, addIssue);
      if (
        fieldType === FieldType.DATE &&
        !isCatalogQueryValue(value) &&
        comparatorType !== MatchesCatalogComparator.MATCHES_CATALOG_DATA
      ) {
        validateDateValue(
          { value: value as string | DateRange, fieldType, comparatorType },
          addIssue,
        );
      } else if (!isObject(value)) {
        validateRegexValue({ value, comparatorType }, addIssue);
        validateNumberValue({ value, values, fieldType, castType, comparatorType }, addIssue);
      }
    })
    .transform(
      flow(
        transformCatalogCondition,
        transformCampaignLabelCondition,
        transformToUnsubscribedMessageCondition,
      ),
    );
};
